import { Link } from "react-router-dom";
import type { Vehicle } from "@/types";
import { formatKES, vehicleTitle } from "@/lib/format";
import StarRating from "@/components/base/StarRating";

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

export default function VehicleCard({ vehicle }: { vehicle: Vehicle }) {
  const image = vehicle.images?.[0]?.url || FALLBACK_IMAGE;
  const title = vehicleTitle(vehicle.make, vehicle.model, vehicle.year);

  return (
    <Link
      to={`/vehicle/${vehicle.id}`}
      className="group block overflow-hidden rounded-2xl surface-card transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/60"
    >
      <div className="relative h-52 w-full overflow-hidden bg-background-100">
        <img
          src={image}
          alt={`${title} for hire in ${vehicle.city}`}
          title={`${vehicle.make} ${vehicle.model} car rental in ${vehicle.city} — Gari`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background-50/95 to-transparent" />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {vehicle.instant_book && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500/90 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-flashlight-fill"></i> Instant book
            </span>
          )}
          {vehicle.delivery && (
            <span className="inline-flex items-center gap-1 rounded-full bg-background-50/80 px-2.5 py-1 text-xs font-medium text-foreground-900 backdrop-blur">
              <i className="ri-map-pin-2-line"></i> Delivery
            </span>
          )}
        </div>
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-background-50/80 px-2.5 py-1 text-xs font-medium text-foreground-950 backdrop-blur">
          <i className="ri-star-fill text-primary-500"></i>
          {Number(vehicle.rating ?? 0).toFixed(2)}
        </span>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-base font-semibold text-foreground-950">{title}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-map-pin-line text-foreground-500"></i>
              <span className="truncate">{vehicle.location || vehicle.city}</span>
            </p>
          </div>
          <span className="shrink-0 rounded-md bg-secondary-100/70 px-2 py-1 text-xs font-medium text-secondary-900">
            {vehicle.category}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-settings-3-line"></i> {vehicle.transmission}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-3-line"></i> {vehicle.seats} seats
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-gas-station-line"></i> {vehicle.fuel_type}
          </span>
        </div>

        <div className="mt-4 flex items-end justify-between border-t border-background-200/70 pt-3">
          <StarRating rating={Number(vehicle.rating ?? 0)} count={vehicle.reviews_count} />
          <p className="text-right">
            <span className="font-heading text-lg font-semibold text-foreground-950">
              {formatKES(vehicle.price_per_day)}
            </span>
            <span className="text-xs text-foreground-500"> /day</span>
          </p>
        </div>
      </div>
    </Link>
  );
}